import React, { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import emailjs from '@emailjs/browser';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  FaShieldAlt,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaHome,
  FaHandsHelping,
  FaSpinner,
  FaCheckCircle,
  FaExclamationCircle
} from 'react-icons/fa';
import { ALCALDIAS_OPTIONS, SERVICIOS_OPTIONS, VALIDATION_PATTERNS, COMPANY_INFO } from '../constants';
import Seo from './common/Seo';

const initialFormData = {
  name: '',
  email: '',
  phone: '',
  alcaldia: '',
  colonia: '',
  servicio: '',
  mensaje: ''
};

const InputField = ({ 
  label, 
  id, 
  type, 
  value, 
  onChange, 
  placeholder, 
  required, 
  autoComplete, 
  options, 
  error, 
  icon 
}) => {
  const inputClasses = `w-full pl-10 p-3 border-2 rounded-lg focus:outline-none focus:ring-2 ${
    error ? 'border-red-500 focus:ring-red-300' : 'border-gray-300 focus:ring-blue-400'
  }`;

  const Icon = icon || (() => null);

  return (
    <div className="mb-6 relative">
      <label htmlFor={id} className="block text-gray-700 font-semibold mb-2">
        {label}{required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500">
          <Icon />
        </div>
        {type === 'select' ? (
          <select
            id={id}
            name={id}
            value={value}
            onChange={onChange}
            required={required}
            autoComplete={autoComplete}
            className={inputClasses}
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : type === 'textarea' ? (
          <textarea
            id={id}
            name={id}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            required={required}
            autoComplete={autoComplete}
            className={`${inputClasses} h-32 resize-none`}
            rows="4"
          />
        ) : (
          <input
            type={type}
            id={id}
            name={id}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            required={required}
            autoComplete={autoComplete}
            className={inputClasses}
          />
        )}
      </div>
      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};

InputField.propTypes = {
  label: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  autoComplete: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string
  })),
  error: PropTypes.string,
  icon: PropTypes.elementType
};

function ContactPage() {
  const [formData, setFormData] = useState(initialFormData);
  const [status, setStatus] = useState({ type: null, message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [ setFormTouched] = useState(false);
  const recaptchaRef = useRef(null);
  const formRef = useRef(null);

  // Validación memoizada con useCallback
  const validateForm = useCallback(() => {
    const newErrors = {};

    // Validación mejorada del nombre
    if (!formData.name.trim()) {
      newErrors.name = 'Nombre completo requerido';
    } else if (!VALIDATION_PATTERNS.name.test(formData.name.trim())) {
      newErrors.name = 'Por favor ingresa nombre y apellido válidos (mínimo dos palabras de al menos 3 caracteres)';
    }

    if (!VALIDATION_PATTERNS.email.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (!VALIDATION_PATTERNS.phone.test(formData.phone)) {
      newErrors.phone = 'Teléfono inválido (10 dígitos)';
    }

    if (!formData.alcaldia) {
      newErrors.alcaldia = 'Selecciona una alcaldía';
    }

    if (!formData.colonia.trim()) {
      newErrors.colonia = 'Colonia requerida';
    } else if (formData.colonia.trim().length < 3) {
      newErrors.colonia = 'Colonia debe tener al menos 3 caracteres';
    }

    if (!formData.servicio) {
      newErrors.servicio = 'Selecciona un servicio';
    }

    if (!recaptchaToken) {
      newErrors.recaptcha = 'Por favor verifica que no eres un robot';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, recaptchaToken]);

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
    if (errors.recaptcha) {
      setErrors(prev => ({ ...prev, recaptcha: '' }));
    }
  };

  const handleRecaptchaExpired = () => {
    setRecaptchaToken(null);
    setErrors(prev => ({ ...prev, recaptcha: 'reCAPTCHA expirado, por favor verifica nuevamente' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setFormTouched(true);
    const isValid = validateForm();
    if (!isValid) {
      // Desplazar al primer error
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const element = document.getElementById(firstErrorKey);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: 'loading', message: 'Verificando y enviando...' });

    try {
      const response = await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          user_name: formData.name,
          user_email: formData.email,
          user_phone: formData.phone,
          user_alcaldia: formData.alcaldia,
          user_colonia: formData.colonia,
          user_servicio: formData.servicio,
          user_mensaje: formData.mensaje,
          recaptcha_token: recaptchaToken
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      if (response.status === 200) {
        setStatus({ type: 'success', message: '¡Formulario enviado con éxito! Nos pondremos en contacto contigo pronto.' });
        setFormData(initialFormData);
        setErrors({});
        setRecaptchaToken(null);
        setFormTouched(false);
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }

        // Ademas del correo (EmailJS, arriba), se manda el mismo formulario
        // a nuestro propio backend (CRM) para que se cree el lead ahi y el
        // bot de WhatsApp le de seguimiento solo. Es "mejor esfuerzo": si
        // esto falla no se le muestra ningun error al usuario -- el correo
        // de arriba ya se mando bien, que es lo que el usuario ve.
        fetch('/api/contacto', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_name: formData.name,
            user_email: formData.email,
            user_phone: formData.phone,
            user_alcaldia: formData.alcaldia,
            user_colonia: formData.colonia,
            user_servicio: formData.servicio,
            user_mensaje: formData.mensaje,
            recaptcha_token: recaptchaToken
          })
        }).catch((err) => {
          console.error('No se pudo registrar el lead en el CRM (el correo sí se envió):', err);
        });
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setStatus({ 
        type: 'error', 
        message: 'Error al enviar el formulario. Por favor intenta nuevamente.' 
      });
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      setRecaptchaToken(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Validación en tiempo real para el teléfono (solo números)
    if (name === 'phone') {
      // Permitir solo números y limitar a 10 caracteres
      const cleanedValue = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({ ...prev, [name]: cleanedValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    setStatus({ type: null, message: '' });
    setFormTouched(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 pt-28 pb-14 px-4 sm:px-6 lg:px-8">
      <Seo
        title="Contacto | Geriatría Serena - Guadalajara"
        description="Contacta a Geriatría Serena para solicitar enfermería y cuidadoras a domicilio en Guadalajara y Zapopan. Responde el formulario o escríbenos por WhatsApp."
        canonical="https://geriatriaserena.com/contact"
        meta={[
          { property: 'og:title', content: 'Contacto | Geriatría Serena' },
          { property: 'og:description', content: 'Solicita información sobre enfermería y cuidado a domicilio en Guadalajara. Formulario de contacto, teléfono y ubicación.' },
          { property: 'og:type', content: 'website' },
          { property: 'og:url', content: 'https://geriatriaserena.com/contact' }
        ]}
      />
      <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-blue-700 py-6 px-8 relative">
          <div className="absolute top-4 right-4 bg-white bg-opacity-20 rounded-full p-2">
            <FaHandsHelping className="text-white text-xl" />
          </div>
          <h1 className="text-3xl font-bold text-white text-center">
            Solicite su Consulta Personalizada
          </h1>
          <p className="text-blue-200 text-center mt-2">
            Complete el formulario y uno de nuestros asesores le contactará a la brevedad
          </p>
        </div>
        
        <div className="p-8">
          <form ref={formRef} onSubmit={handleSubmit} noValidate className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField
                label="Nombre completo"
                id="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                placeholder="Ej. María García López"
                required
                autoComplete="name"
                error={errors.name}
                icon={FaUser}
              />

              <InputField
                label="Email"
                id="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Ej. ejemplo@correo.com"
                required
                autoComplete="email"
                error={errors.email}
                icon={FaEnvelope}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField
                label="Teléfono"
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Ej. 3312345678"
                required
                autoComplete="tel"
                error={errors.phone}
                icon={FaPhone}
              />

              <InputField
                label="Alcaldía"
                id="alcaldia"
                type="select"
                value={formData.alcaldia}
                onChange={handleChange}
                required
                options={ALCALDIAS_OPTIONS}
                error={errors.alcaldia}
                icon={FaMapMarkerAlt}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField
                label="Colonia"
                id="colonia"
                type="text"
                value={formData.colonia}
                onChange={handleChange}
                placeholder="Ej. Colonia Moderna"
                required
                autoComplete="address-level3"
                error={errors.colonia}
                icon={FaHome}
              />

              <InputField
                label="Servicio requerido"
                id="servicio"
                type="select"
                value={formData.servicio}
                onChange={handleChange}
                required
                options={SERVICIOS_OPTIONS}
                error={errors.servicio}
                icon={FaHandsHelping}
              />
            </div>

            <InputField
              label="Mensaje adicional"
              id="mensaje"
              type="textarea"
              value={formData.mensaje}
              onChange={handleChange}
              placeholder="Describe cualquier requerimiento especial (opcional)..."
            />

            {/* reCAPTCHA Section */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <FaShieldAlt className="text-gray-600" />
                <span className="text-gray-700 font-semibold">Verificación de seguridad</span>
                <span className="text-red-500">*</span>
              </div>
              <div className="flex justify-center">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                  onChange={handleRecaptchaChange}
                  onExpired={handleRecaptchaExpired}
                  theme="light"
                  size="normal"
                />
              </div>
              {errors.recaptcha && (
                <p className="text-red-500 text-sm mt-2 text-center">{errors.recaptcha}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting || !recaptchaToken}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4 px-6 rounded-lg font-bold
              hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all
              flex items-center justify-center gap-2 text-lg shadow-md hover:shadow-lg transform hover:-translate-y-0.5
              transition duration-300 ease-in-out"
              aria-live="polite"
            >
              {isSubmitting ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Enviando...
                </>
              ) : (
                'Enviar solicitud'
              )}
            </button>

            {status.message && (
              <div
                role="alert"
                className={`p-4 rounded-lg ${
                  status.type === 'success' ? 'bg-green-100 text-green-800 border border-green-300' :
                  status.type === 'error' ? 'bg-red-100 text-red-800 border border-red-300' :
                  'bg-blue-100 text-blue-800'
                } flex items-center gap-3 mt-4 animate-fadeIn`}
              >
                {status.type === 'success' ? (
                  <FaCheckCircle className="text-green-600 text-xl flex-shrink-0" />
                ) : status.type === 'error' ? (
                  <FaExclamationCircle className="text-red-600 text-xl flex-shrink-0" />
                ) : (
                  <FaSpinner className="animate-spin text-blue-600 text-xl flex-shrink-0" />
                )}
                <span>{status.message}</span>
              </div>
            )}
            
            <div className="text-center text-sm text-gray-500 mt-6">
              <p>Su información es confidencial y nunca será compartida con terceros.</p>
              <p className="mt-1">Protegido por reCAPTCHA — 
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-1">
                  Privacidad
                </a>
                <span className="mx-1">-</span>
                <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  Términos
                </a>
              </p>
            </div>
          </form>
        </div>

        {/* Ubicación en Google Maps */}
        <div className="border-t border-gray-100 p-8 pt-6">
          <div className="flex items-center gap-2 mb-4">
            <FaMapMarkerAlt className="text-blue-600" />
            <h2 className="text-gray-800 font-semibold">Nuestra ubicación</h2>
          </div>
          <div className="rounded-xl overflow-hidden shadow-md border border-gray-200">
            <iframe
              src={COMPANY_INFO.mapsEmbedUrl}
              width="100%"
              height="320"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
              title={`Ubicación de ${COMPANY_INFO.name} en ${COMPANY_INFO.addressShort}`}
            />
          </div>
          <p className="text-sm text-gray-500 mt-3">
            {COMPANY_INFO.addressShort} —{' '}
            <a
              href={COMPANY_INFO.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline font-medium"
            >
              Abrir en Google Maps
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default ContactPage;
